/**
 * useProximity — Sprint 1
 * App-wide background proximity watcher that runs while a trip is active.
 *
 * The core loop:
 *   • Within PROXIMITY_RADIUS_M of a POI  → that POI's id is "revealed"
 *     (its hidden layer of guidance becomes available). Silent.
 *   • Within WHISPER_RADIUS_M of a `hidden` POI → that id is "surfaced" and a
 *     soft, dismissible whisper is offered ("the neighbourhood whispers…").
 *
 * Privacy-first: the device position lives ONLY in memory (lastFix ref). It is
 * never transmitted, never persisted as a track, never logged. The only things
 * that reach storage are the derived id lists (revealedPois / surfacedPois),
 * which carry no coordinates. Once surfaced/revealed, an id stays so for the
 * whole trip — the reveal is the reward; nothing is ever taken away.
 *
 * @param {boolean} active  Run the watcher (there is an active trip).
 * @param {Array}   pois    Geolocated POIs: { id, name, lat, lng, hidden, reveal }.
 * @returns {{
 *   revealedIds: Set<string>, surfacedIds: Set<string>,
 *   whisper: {id:string,name:string}|null, dismissWhisper: () => void,
 *   supported: boolean, error: null|'denied'|'unsupported'|'error'
 * }}
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import Storage from '../utils/storageES';
import {
  PROXIMITY_RADIUS_M,
  WHISPER_RADIUS_M,
  GEO_OPTIONS,
  haversineM,
} from '../utils/reveal';

export default function useProximity(active, pois) {
  const [revealedIds, setRevealedIds] = useState(() => new Set());
  const [surfacedIds, setSurfacedIds] = useState(() => new Set());
  const [whisper, setWhisper] = useState(null);       // { id, name } | null
  const [revealEvent, setRevealEvent] = useState(null); // { name, reveal } | null
  const [error, setError] = useState(null);

  const lastFix = useRef(null);                    // {lat,lng} — memory only
  const watchId = useRef(null);
  const poisRef = useRef(pois || []);
  const revealedRef = useRef(revealedIds);
  const surfacedRef = useRef(surfacedIds);
  const persistTimer = useRef(null);

  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  // Keep refs in sync so the long-lived watcher reads current values without
  // being torn down and recreated on every fix.
  useEffect(() => { poisRef.current = pois || []; }, [pois]);
  useEffect(() => { revealedRef.current = revealedIds; }, [revealedIds]);
  useEffect(() => { surfacedRef.current = surfacedIds; }, [surfacedIds]);

  // Seed sets from the active trip whenever it becomes active.
  useEffect(() => {
    if (!active) {
      setRevealedIds(new Set());
      setSurfacedIds(new Set());
      setWhisper(null);
      setRevealEvent(null);
      return;
    }
    const t = Storage.getTripData();
    setRevealedIds(new Set(t.revealedPois || []));
    setSurfacedIds(new Set(t.surfacedPois || []));
  }, [active]);

  // The watcher. Depends only on `active`; POIs flow in through poisRef.
  useEffect(() => {
    if (!active) return;
    if (!supported) { setError('unsupported'); return; }
    setError(null);

    const onFix = (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      lastFix.current = { lat, lng };              // never logged / never sent
      const list = poisRef.current;
      if (!list.length) return;

      const addReveal = [];
      const addSurface = [];
      let whisperCandidate = null;
      let revealCandidate = null;

      for (const p of list) {
        if (typeof p.lat !== 'number' || typeof p.lng !== 'number') continue;
        const d = haversineM(lat, lng, p.lat, p.lng);
        if (d <= PROXIMITY_RADIUS_M && !revealedRef.current.has(p.id)) {
          addReveal.push(p.id);
          // Hidden gems have no list card of their own — surface their reveal
          // content here. Visible POIs reveal silently in their own card.
          if (p.hidden && p.reveal && !revealCandidate) {
            revealCandidate = { name: p.name, reveal: p.reveal };
          }
        }
        if (p.hidden && d <= WHISPER_RADIUS_M && !surfacedRef.current.has(p.id)) {
          addSurface.push(p.id);
          if (!whisperCandidate) whisperCandidate = { id: p.id, name: p.name };
        }
      }

      if (addReveal.length) {
        setRevealedIds(prev => {
          const n = new Set(prev); addReveal.forEach(i => n.add(i)); return n;
        });
        if (revealCandidate) setRevealEvent(revealCandidate);
      }
      if (addSurface.length) {
        setSurfacedIds(prev => {
          const n = new Set(prev); addSurface.forEach(i => n.add(i)); return n;
        });
        setWhisper(w => w || whisperCandidate);
        // Sprint 4A — let the constellation light up and the postcard layer
        // re-check completion, the moment a gem surfaces. Carries only ids.
        try {
          window.dispatchEvent(new CustomEvent('wayfare-gem-surfaced', { detail: { ids: addSurface } }));
        } catch { /* CustomEvent unavailable → constellation refreshes on next mount */ }
      }
    };

    const onErr = (e) => {
      // Permission denied (1) is a normal, respected user choice — not an error
      // we surface loudly. Just stop trying for this session.
      setError(e && e.code === 1 ? 'denied' : 'error');
    };

    const id = navigator.geolocation.watchPosition(onFix, onErr, GEO_OPTIONS);
    watchId.current = id;
    return () => {
      if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
      lastFix.current = null;                      // drop position on teardown
    };
  }, [active, supported]);

  // Persist derived ids back onto the active trip (debounced, coordinate-free).
  // Spread preserves every other field so concurrent edits aren't clobbered.
  useEffect(() => {
    if (!active) return;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      const t = Storage.getTripData();
      const revealedPois = [...revealedIds];
      const surfacedPois = [...surfacedIds];
      const prevR = t.revealedPois || [];
      const prevS = t.surfacedPois || [];
      const unchanged =
        prevR.length === revealedPois.length &&
        prevS.length === surfacedPois.length &&
        revealedPois.every(i => prevR.includes(i)) &&
        surfacedPois.every(i => prevS.includes(i));
      if (unchanged) return;
      Storage.saveTripData({ ...t, revealedPois, surfacedPois });
    }, 600);
    return () => { if (persistTimer.current) clearTimeout(persistTimer.current); };
  }, [active, revealedIds, surfacedIds]);

  const dismissWhisper = useCallback(() => setWhisper(null), []);
  const dismissReveal = useCallback(() => setRevealEvent(null), []);

  return {
    revealedIds, surfacedIds,
    whisper, dismissWhisper,
    revealEvent, dismissReveal,
    supported, error,
  };
}
